import React, { useState, useEffect } from 'react';
import { Globe, Plus, Search, Copy, Eye, EyeOff, ExternalLink, Trash2, Edit2, Key, Shield, AlertCircle, Save, X, Calendar, AlertTriangle, CalendarClock, MoreHorizontal } from 'lucide-react';
import ClearFiltersButton from './ClearFiltersButton';

import { PortalCredential, CompanyProfile } from '../types';
import { logActivity } from '../services/auditService';
import { apiService } from '../services/apiService';

import { useModal } from './ModalProvider';

const INITIAL_PORTALS: PortalCredential[] = [
  {
    id: '1',
    name: 'ComprasNet (Compras.gov.br)',
    url: 'https://www.gov.br/compras/pt-br',
    login: 'licita.admin',
        // Do not keep real passwords in source — keep blank placeholder
        password: '',
    updatedAt: new Date().toISOString(),
        renewalDate: new Date(new Date().setMonth(new Date().getMonth() + 12)).toISOString().split('T')[0],
        companyId: '1'
  },
  {
    id: '2',
    name: 'Licitações-E (Banco do Brasil)',
    url: 'https://www.licitacoes-e.com.br',
    login: 'empresa_vencedora',
        password: '',
    updatedAt: new Date().toISOString()
        ,companyId: '1'
  },
  {
    id: '3',
    name: 'Portal de Compras Públicas',
    url: 'https://www.portaldecompraspublicas.com.br',
    login: 'usuario.master',
        password: '',
    updatedAt: new Date().toISOString(),
    renewalDate: new Date(new Date().setDate(new Date().getDate() + 5)).toISOString().split('T')[0] // Expira em 5 dias (teste)
        ,companyId: '1'
  }
];

const PORTAL_PRESETS = [
  {
    name: 'Compras.gov.br',
    url: 'https://www.gov.br/compras/pt-br',
    authType: 'USER_PASSWORD_CERTIFICATE',
    notes: 'Normalmente exige acesso gov.br/e-CNPJ ou certificado para ações sensíveis.',
  },
  {
    name: 'Portal de Compras Públicas',
    url: 'https://www.portaldecompraspublicas.com.br',
    authType: 'USER_PASSWORD',
    notes: 'Também conhecido como compraspublicas.com.br.',
  },
  {
    name: 'Compras BR',
    url: 'https://comprasbr.com.br',
    authType: 'USER_PASSWORD',
    notes: 'Configurar usuário habilitado para sala de disputa e mensagens.',
  },
  {
    name: 'BNC - Bolsa Nacional de Compras',
    url: 'https://bnc.org.br',
    authType: 'USER_PASSWORD',
    notes: 'Portal BNC: validar usuário de disputa, avisos e chat.',
  },
  {
    name: 'BLL Compras',
    url: 'https://bllcompras.com',
    authType: 'USER_PASSWORD',
    notes: 'Portal BLL: validar credenciais de operador e sala de disputa.',
  },
  {
    name: 'Licitanet',
    url: 'https://licitanet.com.br',
    authType: 'USER_PASSWORD',
    notes: 'Validar acesso ao chat do pregão e mensagens do pregoeiro.',
  },
] as const;

const AUTH_TYPE_LABELS: Record<string, string> = {
  USER_PASSWORD: 'Usuário e senha',
  CERTIFICATE: 'Certificado digital',
  USER_PASSWORD_CERTIFICATE: 'Usuário, senha e certificado',
};

const PortalManager: React.FC = () => {
    const [portals, setPortals] = useState<PortalCredential[]>([]);
    const [companies, setCompanies] = useState<CompanyProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [visiblePasswords, setVisiblePasswords] = useState<Set<string>>(new Set());
    const [openActionsId, setOpenActionsId] = useState<string | null>(null);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPortal, setEditingPortal] = useState<PortalCredential | null>(null);
  const [formData, setFormData] = useState<Partial<PortalCredential>>({});

    const { showAlert, showConfirm } = useModal();

    useEffect(() => {
        const load = async () => {
            try {
                const [list, comps] = await Promise.all([apiService.getPortals(), apiService.getCompanies()]);
                if (comps && comps.length > 0) setCompanies(comps);
                if (list && list.length > 0) {
                    setPortals(list);
                } else {
                    setPortals(INITIAL_PORTALS);
                }
            } catch (err) {
                console.error('Erro ao carregar portais:', err);
                // fallback local
                setPortals(INITIAL_PORTALS);
            }
        };
        load();
    }, []);

    // Fecha menus de ações quando clicar fora
    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target) return;
            // se clicar em botão/menu não fecha
            if (target.closest('.portal-actions-menu') || target.closest('.portal-actions-button')) return;
            if (openActionsId) setOpenActionsId(null);
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, [openActionsId]);

    // savePortals removed: now persisted via API

  const togglePasswordVisibility = (id: string) => {
    setVisiblePasswords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
        // Log de segurança quando a senha é revelada
        logActivity('SEGURANCA_VISUALIZACAO', `Senha visualizada para o portal ID: ${id}`, 'WARNING');
      }
      return newSet;
    });
  };

  const copyToClipboard = (text: string, type: 'Login' | 'Senha', portalName: string) => {
    navigator.clipboard.writeText(text);
    // Log da ação
    if (type === 'Senha') {
        logActivity('SEGURANCA_COPIA', `Senha copiada para área de transferência: ${portalName}`, 'WARNING');
    }
  };

  const handleOpenModal = (portal?: PortalCredential) => {
    if (portal) {
      setEditingPortal(portal);
      setFormData({
        ...portal,
        renewalDate: portal.renewalDate ? String(portal.renewalDate).split('T')[0] : '',
        certificateExpiresAt: portal.certificateExpiresAt ? String(portal.certificateExpiresAt).split('T')[0] : '',
      });
    } else {
      setEditingPortal(null);
            setFormData({
                name: '',
                url: '',
                login: '',
                password: '',
                authType: 'USER_PASSWORD',
                certificateAlias: '',
                certificateExpiresAt: '',
                notes: '',
                renewalDate: '',
                companyId: companies && companies.length ? companies[0].id : undefined,
            });
    }
    setIsModalOpen(true);
  };

    const clearFilters = () => {
        setSearchTerm('');
    };

    const handleSave = async () => {
        const authType = formData.authType || 'USER_PASSWORD';
        if (!formData.name || (authType !== 'CERTIFICATE' && !formData.login)) {
                await showAlert(authType === 'CERTIFICATE' ? 'Nome do portal é obrigatório.' : 'Nome e Login são obrigatórios.');
                return;
        }
        if (!formData.companyId) {
            await showAlert('É necessário vincular o acesso a uma empresa.');
            return;
        }

        const payload = { ...formData, updatedAt: new Date().toISOString() } as any;
        (async () => {
            try {
                if (editingPortal) {
                    const updated = await apiService.updatePortal(editingPortal.id, payload);
                    setPortals(prev => prev.map(p => p.id === editingPortal.id ? updated : p));
                    logActivity('PORTAL_ATUALIZACAO', `Credenciais atualizadas para: ${formData.name}`, 'INFO');
                } else {
                    const created = await apiService.createPortal(payload);
                    setPortals(prev => [...prev, created]);
                    logActivity('PORTAL_CRIACAO', `Novo portal cadastrado: ${formData.name}`, 'SUCCESS');
                }
                setIsModalOpen(false);
                } catch (err) {
                console.error('Erro ao salvar portal:', err);
                await showAlert('Erro ao salvar portal. Tente novamente.');
            }
        })();
  };

  const handleDelete = (id: string, name: string) => {
        (async () => {
            const ok = await showConfirm(`Tem certeza que deseja remover as credenciais de ${name}?`);
            if (!ok) return;
            try {
                await apiService.deletePortal(id);
                setPortals(prev => prev.filter(p => p.id !== id));
                logActivity('PORTAL_REMOCAO', `Portal removido: ${name}`, 'WARNING');
            } catch (err) {
                console.error('Erro ao remover portal:', err);
                await showAlert('Erro ao remover portal.');
            }
        })();
  };

  // Helper para calcular dias restantes
  const getDaysRemaining = (dateString?: string) => {
    if (!dateString) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateString);
    target.setHours(0, 0, 0, 0);
    const diff = target.getTime() - today.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const filteredPortals = portals.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              Gerenciador de Acessos
            </h2>
            <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mt-1">Centralize logins e senhas dos portais de licitação em um ambiente seguro.</p>
          </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handleOpenModal()}
                            className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium transition-colors shadow-sm"
                        >
                            <Plus className="w-4 h-4" />
                            Adicionar Portal
                        </button>
                        <ClearFiltersButton onClear={clearFilters} visible={Boolean(searchTerm && searchTerm.trim())} />
                    </div>
        </div>

        {/* Busca e Alerta */}
        <div className="mt-6 flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                </div>
                <input
                    type="text"
                    placeholder="Buscar portal por nome ou URL..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none"
                />
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-xs rounded-lg border border-yellow-100">
                <AlertCircle className="w-4 h-4" />
                <span>Guarde apenas credenciais autorizadas. Certificados devem ficar no token, computador ou cofre seguro usado pela automação.</span>
            </div>
        </div>
      </div>

      {/* Grid de Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredPortals.map((portal) => {
            const daysRemaining = getDaysRemaining(portal.renewalDate);
            const company = companies.find(c => c.id === portal.companyId);
            const companyLabel = company ? (company.tradeName || company.corporateName) : null;
            const isExpired = daysRemaining !== null && daysRemaining < 0;
            const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 7;
            const isWarning = daysRemaining !== null && daysRemaining > 7 && daysRemaining <= 30;

            return (
            <div key={portal.id} className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark hover:shadow-md transition-shadow overflow-hidden group relative">
                {/* Indicador de Status na Borda */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${
                    isExpired ? 'bg-red-500' : isUrgent ? 'bg-orange-500' : isWarning ? 'bg-amber-400' : 'bg-transparent'
                }`}></div>

                <div className="p-5 pl-6">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-300">
                                <Globe className="w-6 h-6" />
                            </div>
                            <div className="overflow-hidden">
                                <h3 className="font-semibold text-ink-title dark:text-ink-title-dark truncate" title={portal.name}>{portal.name}</h3>
                                {companyLabel ? (
                                  <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark truncate">{companyLabel}</div>
                                ) : null}
                                <div className="text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark truncate">
                                    {AUTH_TYPE_LABELS[portal.authType || 'USER_PASSWORD'] || 'Usuário e senha'}
                                </div>
                                <a
                                    href={portal.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs text-blue-500 dark:text-blue-400 hover:underline flex items-center gap-1 truncate"
                                >
                                    {portal.url.replace(/^https?:\/\//, '')}
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                        </div>
                        <div className="relative opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                                onClick={(e) => { e.stopPropagation(); setOpenActionsId(openActionsId === portal.id ? null : portal.id); }}
                                aria-expanded={openActionsId === portal.id}
                                aria-haspopup="menu"
                                className="portal-actions-button p-1.5 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:bg-surface-dark-alt rounded"
                                title="Mais ações"
                            >
                                <MoreHorizontal className="w-4 h-4" />
                            </button>

                            {openActionsId === portal.id && (
                                <div
                                    role="menu"
                                    aria-label={`Ações para ${portal.name}`}
                                    className="portal-actions-menu absolute right-0 mt-2 w-44 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded shadow-md z-20 py-1"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <button
                                        role="menuitem"
                                        onClick={() => { setOpenActionsId(null); handleOpenModal(portal); }}
                                        className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-surface-alt dark:bg-surface-dark-alt"
                                    >
                                        <Edit2 className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" />
                                        Editar
                                    </button>
                                    <button
                                        role="menuitem"
                                        onClick={() => { setOpenActionsId(null); handleDelete(portal.id, portal.name); }}
                                        className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-red-600 dark:text-red-300 hover:bg-red-50"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Remover
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Alertas de Renovação */}
                    {(isExpired || isUrgent || isWarning) && (
                        <div className={`mb-4 px-3 py-2 rounded-lg flex items-center gap-2 text-xs font-bold ${
                            isExpired ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-100 dark:border-red-800/50' :
                            isUrgent ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-brand-200 dark:border-[rgba(203,106,30,0.30)]' :
                            'bg-amber-50 text-amber-700 border border-amber-100'
                        }`}>
                            {isExpired ? <AlertCircle className="w-3.5 h-3.5" /> : <CalendarClock className="w-3.5 h-3.5" />}
                            {isExpired ? `Assinatura Vencida há ${Math.abs(daysRemaining!)} dias` :
                             isUrgent ? `Renovar Assinatura: Vence em ${daysRemaining} dias` :
                             `Atenção: Vence em ${daysRemaining} dias`}
                        </div>
                    )}

                    <div className="space-y-3">
                        {/* Campo Login */}
                        <div className="bg-surface-alt dark:bg-surface-dark-alt rounded-lg p-3 border border-line dark:border-line-dark">
                            <span className="text-[10px] text-ink-subtitle dark:text-ink-subtitle-dark uppercase font-bold tracking-wider mb-1 block">Usuário / Login</span>
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-ink-text dark:text-ink-text-dark truncate select-all">{portal.login}</span>
                                <button
                                    onClick={() => copyToClipboard(portal.login, 'Login', portal.name)}
                                    className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark p-1 rounded hover:bg-surface-alt dark:bg-surface-dark-alt transition-colors"
                                    title="Copiar Usuário"
                                >
                                    <Copy className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>

                        {/* Campo Senha */}
                        <div className="bg-surface-alt dark:bg-surface-dark-alt rounded-lg p-3 border border-line dark:border-line-dark">
                             <span className="text-[10px] text-ink-subtitle dark:text-ink-subtitle-dark uppercase font-bold tracking-wider mb-1 block flex items-center gap-1">
                                Senha
                                {visiblePasswords.has(portal.id) && <span className="text-red-500 dark:text-red-400 text-[9px] lowercase font-normal">(visível)</span>}
                             </span>
                            <div className="flex justify-between items-center">
                                <span className={`text-sm font-medium truncate ${visiblePasswords.has(portal.id) ? 'text-ink-text dark:text-ink-text-dark' : 'text-ink-placeholder dark:text-ink-placeholder-dark tracking-widest'}`}>
                                    {visiblePasswords.has(portal.id) ? (portal.password || '---') : '••••••••••••'}
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => togglePasswordVisibility(portal.id)}
                                        className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-blue-600 p-1 rounded hover:bg-surface-alt dark:bg-surface-dark-alt transition-colors"
                                        title={visiblePasswords.has(portal.id) ? "Ocultar Senha" : "Ver Senha"}
                                    >
                                        {visiblePasswords.has(portal.id) ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                    </button>
                                    <button
                                        onClick={() => copyToClipboard(portal.password || '', 'Senha', portal.name)}
                                        className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark p-1 rounded hover:bg-surface-alt dark:bg-surface-dark-alt transition-colors"
                                        title="Copiar Senha"
                                    >
                                        <Copy className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        {(portal.authType === 'CERTIFICATE' || portal.authType === 'USER_PASSWORD_CERTIFICATE') && (
                            <div className="bg-surface-alt dark:bg-surface-dark-alt rounded-lg p-3 border border-line dark:border-line-dark">
                                <span className="text-[10px] text-ink-subtitle dark:text-ink-subtitle-dark uppercase font-bold tracking-wider mb-1 block">Certificado</span>
                                <div className="text-sm font-medium text-ink-text dark:text-ink-text-dark truncate">
                                    {portal.certificateAlias || 'Certificado não informado'}
                                </div>
                                {portal.certificateExpiresAt ? (
                                    <div className="text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark mt-1">
                                        Validade: {new Date(portal.certificateExpiresAt).toLocaleDateString('pt-BR')}
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 pt-3 border-t border-line dark:border-line-dark flex justify-between items-start gap-2">
                        {portal.notes ? (
                            <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark italic line-clamp-2">"{portal.notes}"</p>
                        ) : <span></span>}

                        {portal.renewalDate && !isExpired && !isUrgent && !isWarning && (
                            <span className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark flex items-center gap-1 whitespace-nowrap bg-surface-alt dark:bg-surface-dark-alt px-1.5 py-0.5 rounded">
                                <Calendar className="w-3 h-3" />
                                Renova: {new Date(portal.renewalDate).toLocaleDateString('pt-BR')}
                            </span>
                        )}
                    </div>
                </div>
                <div className="bg-surface-alt dark:bg-surface-dark-alt px-5 py-2 border-t border-line dark:border-line-dark flex justify-between items-center pl-6">
                    <span className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark">Atualizado em {new Date(portal.updatedAt).toLocaleDateString()}</span>
                    <Shield className="w-3 h-3 text-green-500 dark:text-green-400 opacity-50" />
                </div>
            </div>
            );
        })}

        {filteredPortals.length === 0 && (
            <div className="col-span-full py-12 text-center text-ink-subtitle dark:text-ink-subtitle-dark flex flex-col items-center">
                <Globe className="w-12 h-12 text-ink-placeholder dark:text-ink-placeholder-dark mb-2" />
                <p>Nenhum portal encontrado.</p>
                <button onClick={() => handleOpenModal()} className="text-blue-600 dark:text-blue-300 text-sm hover:underline mt-2">
                    Cadastrar o primeiro portal
                </button>
            </div>
        )}
      </div>

      {/* Modal Add/Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
                <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt flex-shrink-0">
                    <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                        <Key className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                        {editingPortal ? 'Editar Acesso' : 'Novo Portal'}
                    </h3>
                    <button onClick={() => setIsModalOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                    {!editingPortal && (
                        <div>
                            <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Portal pronto</label>
                            <select
                                className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                                value=""
                                onChange={e => {
                                    const preset = PORTAL_PRESETS.find(p => p.name === e.target.value);
                                    if (!preset) return;
                                    setFormData({
                                        ...formData,
                                        name: preset.name,
                                        url: preset.url,
                                        authType: preset.authType,
                                        notes: preset.notes,
                                    });
                                }}
                            >
                                <option value="">Selecionar modelo...</option>
                                {PORTAL_PRESETS.map(preset => (
                                    <option key={preset.name} value={preset.name}>{preset.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div>
                        <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Nome do Portal *</label>
                        <input
                            type="text"
                            className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                            placeholder="Ex: ComprasNet"
                            value={formData.name || ''}
                            onChange={e => setFormData({...formData, name: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">URL (Link) do Portal</label>
                        <input
                            type="url"
                            className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                            placeholder="https://..."
                            value={formData.url || ''}
                            onChange={e => setFormData({...formData, url: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Tipo de acesso</label>
                        <select
                            className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                            value={formData.authType || 'USER_PASSWORD'}
                            onChange={e => setFormData({...formData, authType: e.target.value as any})}
                        >
                            <option value="USER_PASSWORD">Usuário e senha</option>
                            <option value="CERTIFICATE">Certificado digital</option>
                            <option value="USER_PASSWORD_CERTIFICATE">Usuário, senha e certificado</option>
                        </select>
                    </div>
                    {/* Empresa vinculada */}
                    <div>
                        <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Empresa *</label>
                        <select
                            className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                            value={formData.companyId || ''}
                            onChange={e => setFormData({...formData, companyId: e.target.value})}
                        >
                            {companies.length === 0 && <option value="">Nenhuma empresa cadastrada</option>}
                            {companies.map(c => (
                                <option key={c.id} value={c.id}>{c.tradeName || c.corporateName}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">
                            Usuário / Login {(formData.authType || 'USER_PASSWORD') === 'CERTIFICATE' ? '' : '*'}
                        </label>
                        <input
                            type="text"
                            className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                            placeholder="Seu usuário"
                            value={formData.login || ''}
                            onChange={e => setFormData({...formData, login: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Senha</label>
                        <input
                            type="text"
                            className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none font-mono"
                            placeholder="Sua senha"
                            value={formData.password || ''}
                            onChange={e => setFormData({...formData, password: e.target.value})}
                        />
                        <p className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark mt-1">Dica: Use um gerenciador de senhas para maior segurança.</p>
                    </div>

                    {((formData.authType || 'USER_PASSWORD') === 'CERTIFICATE' || formData.authType === 'USER_PASSWORD_CERTIFICATE') && (
                        <div className="bg-amber-50 dark:bg-amber-900/30 p-3 rounded-lg border border-amber-100 dark:border-amber-800/50 space-y-3">
                            <div>
                                <label className="block text-xs font-bold text-amber-800 dark:text-amber-200 mb-1 flex items-center gap-1">
                                    <Shield className="w-3 h-3" />
                                    Apelido do certificado
                                </label>
                                <input
                                    type="text"
                                    className="w-full border border-amber-200 dark:border-amber-800/60 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                                    placeholder="Ex: e-CNPJ Soltech A1 2026"
                                    value={formData.certificateAlias || ''}
                                    onChange={e => setFormData({...formData, certificateAlias: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-amber-800 dark:text-amber-200 mb-1">Validade do certificado</label>
                                <input
                                    type="date"
                                    className="w-full border border-amber-200 dark:border-amber-800/60 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                                    value={formData.certificateExpiresAt || ''}
                                    onChange={e => setFormData({...formData, certificateExpiresAt: e.target.value})}
                                />
                            </div>
                            <p className="text-[10px] text-amber-700 dark:text-amber-200">
                                Cadastre aqui apenas a referência do certificado. O arquivo A1/A3 e a senha devem ficar no computador, token ou cofre seguro usado pelo robô de acesso.
                            </p>
                        </div>
                    )}

                    {/* Novo Campo Data de Renovação */}
                    <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg border border-blue-100">
                        <label className="block text-xs font-bold text-blue-800 dark:text-blue-200 mb-1 flex items-center gap-1">
                            <CalendarClock className="w-3 h-3" />
                            Data de Renovação / Validade
                        </label>
                        <input
                            type="date"
                            className="w-full border border-blue-200 dark:border-blue-800/60 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                            value={formData.renewalDate || ''}
                            onChange={e => setFormData({...formData, renewalDate: e.target.value})}
                        />
                        <p className="text-[10px] text-blue-600 dark:text-blue-300 mt-1">
                            O sistema avisará quando o cadastro ou senha estiver perto de expirar.
                        </p>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Notas / Observações</label>
                        <textarea
                            className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none resize-none h-20"
                            placeholder="Ex: Token vence dia 20..."
                            value={formData.notes || ''}
                            onChange={e => setFormData({...formData, notes: e.target.value})}
                        />
                    </div>
                </div>

                <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3 flex-shrink-0">
                    <button
                        onClick={() => setIsModalOpen(false)}
                        className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 flex items-center gap-2 shadow-sm"
                    >
                        <Save className="w-4 h-4" />
                        Salvar
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default PortalManager;
